import { useTranslation } from "react-i18next";

import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { qk } from "@/api/query-keys";
import { notifySuccess } from "@/lib/notify";
import type { MetadataPath } from "@/domain/process-model/metadata-paths";

export type SaveMetadataPathsInput = {
  readonly processModelId: string;
  readonly paths: readonly MetadataPath[];
};

/**
 * 案件一覧・詳細に出す項目の宣言（`metadata_extraction_paths`）を保存する。
 *
 * **宣言だけを送る。** backend は渡された項目だけを更新するので、`display_name` や
 * `primary_file_name` を一緒に送ると画面が持っている古い値で上書きしてしまう。
 *
 * 反映は**これ以降に起票された案件**から。既存案件への backfill は backend の機能が
 * あるが、`SPIFFWORKFLOW_BACKEND_PROCESS_INSTANCE_METADATA_BACKFILL_ENABLED` と
 * celery が要る（ローカル・現構成では無効）。
 */
export async function saveMetadataPaths(
  client: ApiClient,
  input: SaveMetadataPathsInput,
): Promise<void> {
  await client.put(
    `/process-models/${toModifiedProcessModelId(input.processModelId)}`,
    { metadata_extraction_paths: input.paths },
  );
}

export function useSaveMetadataPaths(): UseMutationResult<
  void,
  Error,
  SaveMetadataPathsInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (input: SaveMetadataPathsInput) =>
      saveMetadataPaths(client, input),
    onSuccess: async () => {
      notifySuccess(t("toast.saved"));
      await queryClient.invalidateQueries({
        queryKey: qk.processModels._root(),
      });
    },
  });
}
